import React, { useRef, useState, useEffect } from "react";
import { Button, Space } from "antd";
import { PlusOutlined, MinusOutlined, RedoOutlined } from "@ant-design/icons";

const ImageViewer = ({ fileList }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [initialScale, setInitialScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastTap, setLastTap] = useState(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isAnimating, setIsAnimating] = useState(true);

  const updateContainerSize = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setContainerSize({ width: rect.width, height: rect.height });
    }
  };

  useEffect(() => {
    updateContainerSize();
    window.addEventListener("resize", updateContainerSize);
    return () => window.removeEventListener("resize", updateContainerSize);
  }, []);

  const centerImage = () => {
    const centerX = containerSize.width / 2;
    const centerY = containerSize.height / 2;
    setPosition({ x: centerX, y: centerY });
  };

  const handleZoom = (delta, focalPoint) => {
    setIsAnimating(true);
    setScale((prevScale) => {
      const newScale = Math.min(
        Math.max(prevScale + delta, initialScale * 0.5),
        initialScale * 3
      );
      if (!focalPoint || !containerRef.current) return newScale;

      const rect = containerRef.current.getBoundingClientRect();
      const offsetX = focalPoint.x - rect.left;
      const offsetY = focalPoint.y - rect.top;

      const dx = offsetX - containerSize.width / 2;
      const dy = offsetY - containerSize.height / 2;

      const scaleRatio = newScale / prevScale;
      setPosition((prev) => ({
        x: prev.x - dx * (scaleRatio - 1),
        y: prev.y - dy * (scaleRatio - 1),
      }));

      return newScale;
    });
  };

  const handleZoomIn = (e) => {
    e.stopPropagation();
    handleZoom(0.2);
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    handleZoom(-0.2);
  };

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotate((prev) => (prev + 90) % 360);
    centerImage();
  };

  const startDrag = (e) => {
    e.preventDefault();
    setIsAnimating(false);
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const duringDrag = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const endDrag = () => {
    setIsDragging(false);
    setTimeout(() => setIsAnimating(true), 100);
  };

  const handleDoubleClick = (e) => {
    if (e.target.closest("button")) return;
    const focal = { x: e.clientX, y: e.clientY };
    const shouldZoomOut = scale > initialScale + 0.1;
    handleZoom(shouldZoomOut ? -0.5 : 0.5, focal);
  };

  const handleTouchEnd = (e) => {
    const now = Date.now();
    if (now - lastTap < 300) {
      const touch = e.changedTouches[0];
      const focal = { x: touch.clientX, y: touch.clientY };
      const shouldZoomOut = scale > initialScale + 0.1;
      handleZoom(shouldZoomOut ? -0.5 : 0.5, focal);
    }
    setLastTap(now);
  };

  useEffect(() => {
    if (fileList?.[0]?.url && containerSize.width && containerSize.height) {
      const img = new Image();
      img.src = fileList[0].url;
      img.onload = () => {
        const scaleX = containerSize.width / img.naturalWidth;
        const scaleY = containerSize.height / img.naturalHeight;
        const minScale = Math.min(scaleX, scaleY, 1); // avoid upscaling
        setInitialScale(minScale);
        setScale(minScale);
        centerImage(img.naturalWidth, img.naturalHeight, minScale);
      };
    }
  }, [containerSize, fileList]);

  return (
    <div
      ref={containerRef}
      onMouseMove={duringDrag}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onDoubleClick={handleDoubleClick}
      onTouchEnd={handleTouchEnd}
      style={{
        width: "95%",
        height: "70%",
        position: "relative",
        overflow: "hidden",
        border: "1px solid #ccc",
        backgroundColor: "#f9f9f9",
        userSelect: "none",
        touchAction: "manipulation",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          backgroundColor: "#00000099",
          color: "#fff",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          zIndex: 1000,
        }}
      >
        Zoom: {(scale * 100).toFixed(0)}%
      </div>
      <img
        alt="Zoomable"
        src={fileList?.[0]?.url || ""}
        onMouseDown={startDrag}
        style={{
          position: "absolute",
          top: position.y,
          left: position.x,
          transform: `translate(-50%, -50%) scale(${scale}) rotate(${rotate}deg)`,
          transformOrigin: "center center",
          cursor: isDragging ? "grabbing" : "grab",
          transition: isAnimating ? "transform 0.3s ease" : "none",
          maxWidth: "none",
          maxHeight: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: 10,
        }}
      >
        <Space>
          <Button icon={<PlusOutlined />} onClick={handleZoomIn} />
          <Button icon={<MinusOutlined />} onClick={handleZoomOut} />
        </Space>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 10,
          right: 10,
        }}
      >
        <Button icon={<RedoOutlined />} onClick={handleRotate} />
      </div>
    </div>
  );
};

export default ImageViewer;
